import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Calendar as CalendarIcon, Trophy, Database, ChevronRight, Trash2, Pencil, Target } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { api } from "@/lib/handball";
import AppHeader from "@/components/AppHeader";
import { SessionEditDialog } from "@/components/Dialogs";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function EvaluateSelector() {
  const { gkId } = useParams();
  const navigate = useNavigate();
  const [goalkeeper, setGoalkeeper] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [mode, setMode] = useState(null); // 'match' | 'period' | 'all' | 'tournament' | null
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name }
  const [deleting, setDeleting] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // session being edited

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/sessions/${deleteTarget.id}`);
      setSessions((prev) => prev.filter((s) => s.id !== deleteTarget.id));
      toast.success("Matchen och all skottdata är borttagen");
      setDeleteTarget(null);
    } catch {
      toast.error("Kunde inte ta bort matchen");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    (async () => {
      const [gksRes, sessRes, tournRes] = await Promise.all([
        api.get("/goalkeepers"),
        api.get("/sessions", { params: { goalkeeper_id: gkId } }),
        api.get("/tournaments", { params: { for_goalkeeper: gkId } }),
      ]);
      const gk = gksRes.data.find((g) => g.id === gkId);
      if (!gk) {
        toast.error("Målvakt hittades ej");
        navigate("/");
        return;
      }
      setGoalkeeper(gk);
      setSessions(sessRes.data);
      setTournaments(tournRes.data);
    })();
  }, [gkId, navigate]);

  const goPeriod = () => {
    if (!fromDate || !toDate) {
      toast.error("Välj både startdatum och slutdatum");
      return;
    }
    const from = format(fromDate, "yyyy-MM-dd");
    const to = format(toDate, "yyyy-MM-dd");
    navigate(`/gk/${gkId}/evaluate/period?from=${from}&to=${to}`);
  };

  if (!goalkeeper) return null;

  return (
    <div className="min-h-screen">
      <AppHeader
        subtitle={`Utvärdera · ${goalkeeper.name}`}
        backTo={`/gk/${gkId}`}
        backLabel="Meny"
      />

      <main className="max-w-[1100px] mx-auto px-6 py-10 space-y-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display mb-2">
            Utvärdera sessioner
          </div>
          <h1 className="font-display font-black uppercase tracking-widest text-3xl md:text-4xl text-white">
            Välj vad du vill analysera
          </h1>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <ModeCard
            testid="mode-match"
            icon={<Trophy size={22} className="text-[#34C759]" />}
            title="Välj match"
            subtitle="Analysera en specifik match eller träning."
            active={mode === "match"}
            onClick={() => setMode(mode === "match" ? null : "match")}
          />
          <ModeCard
            testid="mode-tournament"
            icon={<Target size={22} className="text-[#FFD60A]" />}
            title="Turnering/serie"
            subtitle="Sammanlagd statistik för en turnering eller serie."
            active={mode === "tournament"}
            onClick={() => setMode(mode === "tournament" ? null : "tournament")}
          />
          <ModeCard
            testid="mode-period"
            icon={<CalendarIcon size={22} className="text-white" />}
            title="Tidsperiod"
            subtitle="Matcher och träningar från datum till datum."
            active={mode === "period"}
            onClick={() => setMode(mode === "period" ? null : "period")}
          />
          <ModeCard
            testid="mode-all"
            icon={<Database size={22} className="text-white" />}
            title="All data"
            subtitle="Sammanlagd statistik för alla sessioner."
            active={mode === "all"}
            onClick={() => navigate(`/gk/${gkId}/evaluate/all`)}
          />
        </div>

        {mode === "tournament" && (
          <section className="card-tactical p-5" data-testid="tournament-list-panel">
            <div className="section-hdr mb-4">
              <span className="marker" />
              Välj turnering/serie
            </div>
            {tournaments.length === 0 ? (
              <div className="text-zinc-500 text-sm py-6">
                Inga turneringar skapade ännu. Gå tillbaka till menyn och tryck
                på "Skapa turnering/serie".
              </div>
            ) : (
              <div className="space-y-2">
                {tournaments.map((t) => {
                  const today = new Date().toISOString().slice(0, 10);
                  const isActive = t.end_date >= today;
                  return (
                    <Link
                      key={t.id}
                      to={`/gk/${gkId}/evaluate/tournament/${t.id}`}
                      data-testid={`evaluate-tournament-${t.id}`}
                      className="flex items-center justify-between p-4 border border-[#27272A] hover:border-[#34C759] transition-colors"
                    >
                      <div className="min-w-0">
                        <div className="font-display uppercase tracking-widest text-white flex items-center gap-2 flex-wrap">
                          {t.name}
                          <span
                            className={`text-[9px] tracking-widest font-bold border px-1.5 py-0.5 ${
                              isActive
                                ? "border-[#34C759] text-[#34C759]"
                                : "border-zinc-600 text-zinc-500"
                            }`}
                          >
                            {isActive ? "PÅGÅR" : "AVSLUTAD"}
                          </span>
                        </div>
                        <div className="text-xs text-zinc-500 mt-1">
                          {format(new Date(t.start_date + "T00:00:00"), "d MMM yyyy", { locale: sv })}
                          {" — "}
                          {format(new Date(t.end_date + "T00:00:00"), "d MMM yyyy", { locale: sv })}
                        </div>
                      </div>
                      <ChevronRight size={18} className="text-zinc-500 shrink-0 ml-3" />
                    </Link>
                  );
                })}
              </div>
            )}
          </section>
        )}

        {mode === "match" && (
          <section className="card-tactical p-5" data-testid="match-list-panel">
            <div className="section-hdr mb-4">
              <span className="marker" />
              Välj match / träning
            </div>
            {sessions.length === 0 ? (
              <div className="text-zinc-500 text-sm py-6">
                Inga sessioner ännu. Skapa en session först.
              </div>
            ) : (
              <div className="space-y-2">
                {sessions.map((s) => (
                  <div
                    key={s.id}
                    className="flex items-center justify-between border border-[#27272A] hover:border-[#34C759] transition-colors"
                  >
                    <Link
                      to={`/gk/${gkId}/evaluate/session/${s.id}`}
                      data-testid={`evaluate-session-${s.id}`}
                      className="flex items-center justify-between flex-1 p-4 min-w-0"
                    >
                      <div className="min-w-0">
                        <div className="font-display uppercase tracking-widest text-white flex items-center gap-2 flex-wrap">
                          {s.name}
                          {s.completed && (
                            <span
                              className="text-[9px] tracking-widest font-bold border border-[#FFD60A] text-[#FFD60A] px-1.5 py-0.5"
                              data-testid={`evaluate-badge-completed-${s.id}`}
                            >
                              SLUTFÖRD
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-zinc-500 mt-1">
                          {s.session_type === "match" ? "Match" : "Träning"}
                          {s.opponent ? ` · vs ${s.opponent}` : ""} ·{" "}
                          {format(new Date(s.date), "d MMM yyyy", { locale: sv })}
                        </div>
                      </div>
                      <ChevronRight size={18} className="text-zinc-500 shrink-0 ml-3" />
                    </Link>
                    <button
                      data-testid={`evaluate-edit-session-${s.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditTarget(s);
                      }}
                      className="p-4 border-l border-[#27272A] text-zinc-400 hover:text-white hover:bg-[#171717] transition-colors"
                      aria-label="Redigera match"
                      title="Redigera match"
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      data-testid={`evaluate-delete-session-${s.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteTarget({ id: s.id, name: s.name });
                      }}
                      className="p-4 border-l border-[#27272A] text-[#FF3B30] hover:bg-[#FF3B30]/10 transition-colors"
                      aria-label="Ta bort match"
                      title="Ta bort match"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {mode === "period" && (
          <section className="card-tactical p-5" data-testid="period-panel">
            <div className="section-hdr mb-4">
              <span className="marker" />
              Välj tidsperiod
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <DatePickerField
                label="Från"
                testid="period-from"
                value={fromDate}
                onChange={setFromDate}
              />
              <DatePickerField
                label="Till"
                testid="period-to"
                value={toDate}
                onChange={setToDate}
              />
              <Button
                data-testid="period-go-btn"
                onClick={goPeriod}
                disabled={!fromDate || !toDate}
                className="h-11 rounded-none bg-[#34C759] hover:bg-[#2fb350] text-black font-display uppercase tracking-widest disabled:opacity-40"
              >
                Visa statistik
              </Button>
            </div>
          </section>
        )}
      </main>

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <AlertDialogContent
          data-testid="evaluate-delete-confirm"
          className="bg-[#111] border-[#27272A] text-white rounded-none"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display uppercase tracking-widest text-xl">
              Är du säker?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              <span className="block text-white font-display tracking-wide mb-2">
                {deleteTarget?.name}
              </span>
              All skottdata i den här matchen försvinner permanent och kan inte
              återskapas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel
              data-testid="evaluate-delete-cancel"
              className="rounded-none border-[#3F3F46] bg-transparent hover:bg-[#171717] text-white font-display uppercase tracking-widest"
            >
              Nej, behåll data
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="evaluate-delete-confirm-btn"
              onClick={confirmDelete}
              disabled={deleting}
              className="rounded-none bg-[#FF3B30] hover:bg-[#e33228] text-white font-display uppercase tracking-widest"
            >
              {deleting ? "Tar bort…" : "Ja, ta bort data"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <SessionEditDialog
        open={!!editTarget}
        onOpenChange={(o) => !o && setEditTarget(null)}
        session={editTarget}
        onSaved={(updated) => {
          setSessions((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
          setEditTarget(null);
        }}
      />
    </div>
  );
}

function ModeCard({ icon, title, subtitle, onClick, testid, active }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`card-tactical p-5 text-left h-full flex flex-col transition-colors ${
        active ? "border-[#34C759]" : "hover:border-[#3F3F46]"
      }`}
      style={{ background: active ? "#0f1a12" : undefined }}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 border border-[#3F3F46] flex items-center justify-center bg-[#171717]">
          {icon}
        </div>
        <div className="font-display font-black uppercase tracking-widest text-lg text-white leading-tight">
          {title}
        </div>
      </div>
      <div className="text-sm text-zinc-400">{subtitle}</div>
    </button>
  );
}

function DatePickerField({ label, value, onChange, testid }) {
  return (
    <div className="space-y-2">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-display">
        {label}
      </div>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            data-testid={testid}
            variant="outline"
            className="w-full h-11 rounded-none border-[#3F3F46] bg-black hover:bg-[#171717] text-white font-normal justify-start"
          >
            <CalendarIcon size={16} className="mr-2 text-zinc-400" />
            {value ? format(value, "d MMM yyyy", { locale: sv }) : "Välj datum"}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="bg-[#111] border-[#27272A] p-0"
          align="start"
        >
          <Calendar
            mode="single"
            selected={value}
            onSelect={onChange}
            locale={sv}
            initialFocus
            className="bg-[#111] text-white"
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
